import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface CallDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callData: any;
  onUpdate: () => void;
}

export function CallDetailsDialog({ open, onOpenChange, callData, onUpdate }: CallDetailsDialogProps) {
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    service_interest: "",
    call_type: "",
    lead_quality: "",
    estimated_value: "",
    follow_up_date: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && callData) {
      setFormData({
        customer_name: callData.customer_name || "",
        customer_email: callData.customer_email || "",
        service_interest: callData.service_interest || "",
        call_type: callData.call_type || "",
        lead_quality: callData.lead_quality || "",
        estimated_value: callData.estimated_value || "",
        follow_up_date: callData.follow_up_date ? callData.follow_up_date.split('T')[0] : ""
      });
    }
  }, [open, callData]);

  const handleSubmit = async () => {
    setLoading(true);
    
    const updateData: any = {
      customer_name: formData.customer_name || null,
      customer_email: formData.customer_email || null,
      service_interest: formData.service_interest || null,
      call_type: formData.call_type || null,
      lead_quality: formData.lead_quality || null,
      estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      follow_up_date: formData.follow_up_date || null
    };

    const { error } = await supabase
      .from('phone_leads')
      .update(updateData)
      .eq('id', callData.id);

    if (error) {
      toast.error("Failed to update call details");
      console.error('Error updating call:', error);
    } else {
      toast.success("Call details updated successfully");
      onUpdate();
      onOpenChange(false);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Call Details</DialogTitle>
          <DialogDescription>
            Update customer information and call details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">Customer Name</Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="customer_email">Customer Email</Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="service_interest">Service Interest</Label>
            <Input
              id="service_interest"
              value={formData.service_interest}
              onChange={(e) => setFormData({ ...formData, service_interest: e.target.value })}
              placeholder="Roof Repair, Gutter Installation, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="call_type">Call Type</Label>
              <Select 
                value={formData.call_type} 
                onValueChange={(value) => setFormData({ ...formData, call_type: value })}
              >
                <SelectTrigger id="call_type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inquiry">Inquiry</SelectItem>
                  <SelectItem value="quote_request">Quote Request</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="lead_quality">Lead Quality</Label>
              <Select 
                value={formData.lead_quality} 
                onValueChange={(value) => setFormData({ ...formData, lead_quality: value })}
              >
                <SelectTrigger id="lead_quality">
                  <SelectValue placeholder="Select quality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                  <SelectItem value="unqualified">Unqualified</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estimated_value">Estimated Value ($)</Label>
              <Input
                id="estimated_value"
                type="number"
                step="0.01"
                value={formData.estimated_value}
                onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                placeholder="5000.00"
              />
            </div>

            <div>
              <Label htmlFor="follow_up_date">Follow-up Date</Label>
              <Input
                id="follow_up_date"
                type="date"
                value={formData.follow_up_date}
                onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}